import { LoadingButton } from '@mui/lab';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Alert,
  FormControl,
  TextField,
  Autocomplete,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Checkbox,
  Popper,
  Paper,
} from '@mui/material';
import { FC, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import { AddPlusIcon } from '../../../vectors/index';
import Add from '@mui/icons-material/Add';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCreateCompany } from '../hooks/useCreateCompany';
import isEmail from 'validator/lib/isEmail';
import { useGetPaymantDetails } from '@features/payments/hooks/useGetPaymantDetails';
import { authContext } from '@staffedupusa/shared-components';
import { useNavigate } from 'react-router-dom';
import { PLANS } from '@utils/pricing';
import EnterpriseDetailsSection from './EnterpriseDetailsForm';
import { useUserCountry } from '@context/UserCountryContext';
import tzLookup from 'tz-lookup';
import { notificationReadRoute } from '@features/notifications';

interface PlacePrediction {
  description: string;
  place_id: string;
}

interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

interface PlaceDetails {
  geometry: {
    location: {
      lat: () => number;
      lng: () => number;
    };
  };
  formatted_phone_number?: string;
  vicinity?: string;
  formatted_address?: string;
  address_components: AddressComponent[];
  website?: string;
  name?: string;
  utc_offset?: number;
}

interface PlaceDetailsState {
  s_lat: number;
  s_lon: number;
  phone: string;
  s_address1: string;
  s_city: string;
  s_state: string;
  postal_code: string;
  s_zip: string;
  timezone: string;
  website: string;
  name: string;
}

type AddCompanyModalProps = {
  handleClose: () => void;
  templateId: number;
  billingType: number;
  templateName?: string;
};

const AddCompanyModal: FC<AddCompanyModalProps> = ({
  handleClose,
  templateId,
  billingType,
  templateName,
}) => {
  const navigate = useNavigate();
  const { authData } = useContext(authContext);
  const { userCountry } = useUserCountry();
  const enterprise = authData?.user?.enterprise;
  const [errorText, setErrorText] = useState<string>('');
  const [valueOption, setValueOption] = useState<Object | null>(null);
  const [addressOption, setAddressOption] = useState<Object | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [addressInputValue, setAddressInputValue] = useState<string>('');
  const [options, setOptions] = useState<PlacePrediction[] | []>([]);
  const [useSameEnterpriseDetails, setUseSameEnterpriseDetails] =
    useState<boolean>(false);
  const [addressesOptions, setAddressesOptions] = useState<
    PlacePrediction[] | []
  >([]);
  const [selectedPlaceDetails, setSelectedPlaceDetails] = useState<any>(null);
  const [selectedAddress, setSelectedAddress] = useState<any>(null);
  const [errorsText, setErrorsText] = useState<any>(null);
  const [manualAddressFields, setManualAddressFields] =
    useState<boolean>(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'1' | '2' | '4'>('4');
  const [noPhysicalAddress, setNoPhysicalAddress] = useState(false);
  const [cityOption, setCityOption] = useState<PlacePrediction[] | []>([]);
  const [inputValueCity, setInputValueCity] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<Object | null>(null);
  const [resetInputValue, setResetInputValue] = useState('');
  const { createCompany, isCreatingCompany } = useCreateCompany({
    setErrorText,
  });
  const { paymentInfo } = useGetPaymantDetails();
  const schema = yup.object({
    fullName: yup
      .string()
      .test('fullName-required', 'Name is required', function (value) {
        if (!useSameEnterpriseDetails && !value) {
          return this.createError({ message: 'Name is required' });
        }
        return true;
      }),
    email: yup
      .string()
      .test('email-required', 'Email is required', function (value) {
        if (!useSameEnterpriseDetails) {
          if (!value) {
            return this.createError({ message: 'Email is required' });
          }
          if (!isEmail(value)) {
            return this.createError({ message: 'Must be a valid email' });
          }
        }
        return true;
      }),
    manualAddressFields: yup.boolean(),
    plan_id: yup.number(),

    s_address1: noPhysicalAddress
      ? yup.string()
      : yup
          .string()
          .test('s_address1-required', 'Address is required', function (value) {
            const { manualAddressFields } = this.parent;
            if (manualAddressFields) {
              return !!value; // Ensure s_address1 is not empty if manualAddressFields is true
            }
            return true; // Otherwise, skip validation
          }),

    noPhysicalAddress: yup.boolean(),

    s_city: yup
      .string()
      .test('s_city-required', 'City is required', function (value) {
        const { manualAddressFields, noPhysicalAddress } = this.parent;

        // City is required if either manualAddressFields OR noPhysicalAddress is true
        if (manualAddressFields || noPhysicalAddress) {
          return !!value && value.trim() !== ''; // Ensure s_city is not empty or just whitespace
        }
        return true; // Otherwise, skip validation
      }),

    s_state: yup
      .string()
      .test('s_state-required', 'State is required', function (value) {
        const { manualAddressFields, noPhysicalAddress } = this.parent;
        if (manualAddressFields || noPhysicalAddress) {
          return !!value; // Ensure s_state is not empty if manualAddressFields is true
        }
        return true; // Otherwise, skip validation
      }),

    s_zip: noPhysicalAddress
      ? yup.string()
      : yup
          .string()
          .test('s_zip-required', 'ZIP code is required', function (value) {
            const { manualAddressFields } = this.parent;
            if (manualAddressFields) {
              return !!value; // Ensure s_zip is not empty if manualAddressFields is true
            }
            return true; // Otherwise, skip validation
          }),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      fullName: '',
      email: '',
      s_address1: '',
      s_city: '',
      s_state: '',
      s_zip: '',
    },
  });

  function parseAddress(addressComponents: AddressComponent[]) {
    let postalCode = '';
    let state = '';
    let city = '';
    let streetNumber = '';
    let route = '';
    let remainingAddress = '';
    addressComponents.forEach((component) => {
      if (component.types.includes('postal_code')) {
        postalCode = component.long_name;
      } else if (component.types.includes('administrative_area_level_1')) {
        state = component.long_name;
      } else if (component.types.includes('locality')) {
        city = component.long_name;
      } else if (component.types.includes('street_number')) {
        streetNumber = component.long_name;
      } else if (component.types.includes('route')) {
        route = component.long_name;
      } else {
        remainingAddress += `${component.long_name} `;
      }
    });
    remainingAddress = remainingAddress.trim();
    return {
      city,
      state,
      route,
      postalCode,
      streetNumber,
      remainingAddress,
    };
  }

  const fetchLocations = async (
    query: string,
    fetchAddress?: string,
  ): Promise<void> => {
    if (!query) {
      setOptions([]);
      return;
    }
    const service = new (
      window as any
    ).google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input: query,
        ...((fetchAddress || useSameEnterpriseDetails || noPhysicalAddress) && {
          types: [noPhysicalAddress ? '(cities)' : 'address'],
        }),
        componentRestrictions: { country: userCountry },
      },
      (predictions: PlacePrediction[], status: string) => {
        if (
          status === (window as any).google.maps.places.PlacesServiceStatus.OK
        ) {
          if (noPhysicalAddress) {
            setCityOption(predictions);
          } else {
            fetchAddress
              ? setAddressesOptions(predictions)
              : setOptions(predictions);
          }
        } else {
          console.error('Error fetching locations:', status);
        }
      },
    );
  };

  const fetchPlaceDetails = async (
    placeId: string,
    fetchAddress?: string,
  ): Promise<void> => {
    fetchAddress ? setAddressInputValue('') : setInputValue('');
    const service = new (window as any).google.maps.places.PlacesService(
      document.createElement('div'),
    );

    service.getDetails({ placeId }, (details: PlaceDetails, status: string) => {
      if (
        status === (window as any).google.maps.places.PlacesServiceStatus.OK
      ) {
        const tz = tzLookup(
          details.geometry.location.lat(),
          details.geometry.location.lng(),
        );
        const addressComponents = details.address_components;

        const parsedAddress = parseAddress(addressComponents);

        let streetParts = [];
        let s_address1: string = '';
        if (parsedAddress.streetNumber)
          streetParts.push(parsedAddress.streetNumber);
        if (parsedAddress.route) streetParts.push(parsedAddress.route);
        const addressLines = details.formatted_address
          ? details.formatted_address.split(',')
          : '';
        s_address1 =
          streetParts.length > 0
            ? streetParts.join(', ')
            : addressLines.length > 0
              ? addressLines[0].trim()
              : '';
        const placeDetails: PlaceDetailsState = {
          name: details.name || '',
          s_lat: details.geometry.location.lat(),
          s_lon: details.geometry.location.lng(),
          phone: details.formatted_phone_number || '',
          s_address1: noPhysicalAddress ? '' : s_address1 || '',
          s_city:
            addressComponents.find((comp) => comp.types.includes('locality'))
              ?.long_name || '',
          s_state:
            addressComponents.find((comp) =>
              comp.types.includes('administrative_area_level_1'),
            )?.short_name || '',
          postal_code:
            addressComponents.find((comp) => comp.types.includes('postal_code'))
              ?.long_name || '',
          s_zip:
            addressComponents.find((comp) => comp.types.includes('postal_code'))
              ?.long_name || '',
          timezone: tz ? tz : '',
          website: details.website || '',
        };

        if (useSameEnterpriseDetails) {
          setNoPhysicalAddress(false);
          const enterpriseSplitedEmail = authData?.user?.email.split('@');
          enterpriseSplitedEmail &&
            setValue(
              'email',
              `${enterpriseSplitedEmail[0]}+${placeDetails.s_city
                .replace(/\s+/g, '')
                .toLowerCase()}@${enterpriseSplitedEmail[1]}`,
            );
          enterprise?.name &&
            setInputValue(`${enterprise?.name} ${placeDetails.s_city}`);
        }
        setValue('s_address1', noPhysicalAddress ? '' : details.name || '');
        setValue('s_city', placeDetails.s_city || '');
        setValue('s_state', placeDetails.s_state || '');
        setValue('s_zip', placeDetails.s_zip || '');

        if (details?.name) {
          setAddressInputValue(details.name);
        }

        if (noPhysicalAddress) {
          setSelectedCity({
            description: details.name,
            structured_formatting: { main_text: details.name },
            place_id: placeId,
          });
        } else if (fetchAddress) {
          setNoPhysicalAddress(false);
          setAddressOption({
            description: details.name,
            structured_formatting: { main_text: details.name },
            place_id: placeId,
          });
          setSelectedPlaceDetails(null);
        } else {
          setNoPhysicalAddress(false);
          setValueOption({
            description: `${details.name} ${placeDetails.s_city}`,
            structured_formatting: {
              main_text: `${details.name} ${placeDetails.s_city}`,
            },
            place_id: placeId,
          });
          if (details?.name) {
            setInputValue(`${details.name} ${placeDetails.s_city}`);
          }
          setSelectedPlaceDetails(placeDetails);
          setManualAddressFields(false); // Hide manual fields when selecting from dropdown
        }
      } else {
        console.error('Error fetching place details:', status);
      }
    });
  };

  const handlePlanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedPlan(event.target.value as '1' | '2' | '4');
    }
  };

  const handleAdd = async (data: any) => {
    if (billingType !== 0 && !hasPaymentMethod) {
      toast.error(
        'Please Attach a Payment Method and  billing Cycle to upgrade ',
      );
      navigate('/enterprise/billing/#Payment-Info');
      return;
    }

    if (
      !noPhysicalAddress &&
      !manualAddressFields &&
      !selectedPlaceDetails &&
      !useSameEnterpriseDetails &&
      (!data.s_city ||
        data.s_city.trim() === '' ||
        !data.s_zip ||
        data.s_zip.trim() === '' ||
        !data.s_state ||
        data.s_state.trim() === '' ||
        !inputValue ||
        inputValue.trim() === '')
    ) {
      return setManualAddressFields(true);
    }

    if (
      !noPhysicalAddress &&
      useSameEnterpriseDetails &&
      (!data.s_city ||
        data.s_city.trim() === '' ||
        !data.s_zip ||
        data.s_zip.trim() === '' ||
        !data.s_state ||
        data.s_state.trim() === '' ||
        !inputValue ||
        inputValue.trim() === '')
    ) {
      return setManualAddressFields(true);
    }

    if (!watch('email')) {
      return setErrorText('Email is required');
    } else if (!watch('fullName')) {
      return setErrorText('Name is required');
    } else if (!inputValue) {
      return setErrorText('Busniess Name is required');
    } else {
      setErrorText('');
    }

    if (
      !noPhysicalAddress &&
      !selectedPlaceDetails &&
      manualAddressFields &&
      (((!data.s_address1 || data.s_address1.trim() === '') &&
        (!addressInputValue || addressInputValue.trim() === '')) ||
        !data.s_city ||
        data.s_city.trim() === '' ||
        !data.s_zip ||
        data.s_zip.trim() === '' ||
        !data.s_state ||
        data.s_state.trim() === '' ||
        !inputValue ||
        inputValue.trim() === '')
    ) {
      setErrorsText((prevState: any) => {
        const updatedErrors = { ...prevState };

        if (
          (!data.s_address1 || data.s_address1.trim() === '') &&
          !addressInputValue.trim()
        ) {
          updatedErrors.address = 'Address is required';
        } else if (updatedErrors.hasOwnProperty('address')) {
          delete updatedErrors.address;
        }

        if (!data.s_city || data.s_city.trim() === '') {
          updatedErrors.city = 'City is required';
        } else if (updatedErrors.hasOwnProperty('city')) {
          delete updatedErrors.city;
        }

        if (!data.s_zip || data.s_zip.trim() === '') {
          updatedErrors.zip = 'Zip is required';
        } else if (updatedErrors.hasOwnProperty('zip')) {
          delete updatedErrors.zip;
        }

        if (!data.s_state || data.s_state.trim() === '') {
          updatedErrors.s_state = 'State is required';
        } else if (updatedErrors.hasOwnProperty('s_state')) {
          delete updatedErrors.s_state;
        }

        if (
          !useSameEnterpriseDetails &&
          (!inputValue || inputValue.trim() === '')
        ) {
          updatedErrors.business_name = 'Business name is required';
        } else if (updatedErrors.hasOwnProperty('business_name')) {
          delete updatedErrors.business_name;
        }
        return updatedErrors;
      });

      return;
    }

    if (noPhysicalAddress) {
      // Validate required fields for no physical address scenario
      const validationErrors: any = {};

      if (!data.s_city || data.s_city.trim() === '') {
        validationErrors.city = 'City is required';
      }

      if (!data.s_state || data.s_state.trim() === '') {
        validationErrors.s_state = 'State is required';
      }

      // Note: ZIP is not required when noPhysicalAddress is true

      if (
        !useSameEnterpriseDetails &&
        (!inputValue || inputValue.trim() === '')
      ) {
        validationErrors.business_name = 'Business name is required';
      }

      // If there are validation errors, set them and return
      if (Object.keys(validationErrors).length > 0) {
        setErrorsText(validationErrors);
        return;
      }
    }

    if (noPhysicalAddress === false) {
      if (
        (selectedPlaceDetails?.name !== ' ' &&
          !manualAddressFields &&
          !data.s_city) ||
        data.s_city.trim() === '' ||
        !data.s_zip ||
        data.s_zip.trim() === '' ||
        !data.s_state ||
        data.s_state.trim() === ''
      ) {
        setErrorsText((prevState: any) => {
          const updatedErrors = { ...prevState };

          if (!data.s_city || data.s_city.trim() === '') {
            updatedErrors.city = 'City is required';
          } else if (updatedErrors.hasOwnProperty('city')) {
            delete updatedErrors.city;
          }

          if (!data.s_zip || data.s_zip.trim() === '') {
            updatedErrors.zip = 'Zip is required';
          } else if (updatedErrors.hasOwnProperty('zip')) {
            delete updatedErrors.zip;
          }

          if (!data.s_state || data.s_state.trim() === '') {
            updatedErrors.s_state = 'State is required';
          } else if (updatedErrors.hasOwnProperty('s_state')) {
            delete updatedErrors.s_state;
          }

          return updatedErrors;
        });

        return;
      }
    }

    setErrorsText(null);

    let lat = null;
    let lng = null;
    let timezone = null;

    // Modified condition: Get coordinates for both physical address and no physical address cases
    if (
      noPhysicalAddress || // Added this condition
      !notificationReadRoute ||
      manualAddressFields ||
      useSameEnterpriseDetails ||
      !selectedPlaceDetails
    ) {
      // For noPhysicalAddress, use only city and state for geocoding (zip might be empty)
      const address = noPhysicalAddress
        ? `${data.s_city}, ${data.s_state}` // Only city and state when no physical address
        : `${data.s_address1 || addressInputValue}, ${data.s_city}, ${
            data.s_state
          }, ${data.s_zip}`;

      try {
        const map = new (window as any).google.maps.Map(
          document.createElement('div'),
        );
        const service = new (window as any).google.maps.places.PlacesService(
          map,
        );

        const placeRequest = {
          query: address,
          fields: ['place_id'], // Get only placeId for follow-up
        };

        const placeResult: any = await new Promise((resolve, reject) => {
          service.findPlaceFromQuery(
            placeRequest,
            (results: any, status: any) => {
              if (status === 'OK' && results.length > 0) {
                resolve(results[0]);
              } else {
                reject(`Place lookup failed: ${status}`);
              }
            },
          );
        });

        const placeId = placeResult.place_id;

        if (!placeId) {
          throw new Error('No place ID found.');
        }
        // Get more details using placeId
        const details: any = await new Promise((resolve, reject) => {
          service.getDetails(
            {
              placeId,
              fields: ['geometry', 'utc_offset_minutes'],
            },
            (placeDetails: any, status: any) => {
              if (
                status ===
                (window as any).google.maps.places.PlacesServiceStatus.OK
              ) {
                resolve(placeDetails);
              } else {
                reject(`Details lookup failed: ${status}`);
              }
            },
          );
        });

        if (details.geometry && details.geometry.location) {
          lat = details.geometry.location.lat();
          lng = details.geometry.location.lng();
          const tz = tzLookup(
            details.geometry.location.lat(),
            details.geometry.location.lng(),
          );
          timezone = tz ? tz : '';
        }
      } catch (error) {
        console.error(error);
        toast.error('Please Provide Correct Address');
        return;
      }
    }

    const newPlaceDetail = {
      ...selectedPlaceDetails, // put this first
      s_city: selectedPlaceDetails?.s_city || data.s_city,
      s_state: selectedPlaceDetails?.s_state || data.s_state,
      s_zip: selectedPlaceDetails?.s_zip || data.s_zip || null,
      postal_code: selectedPlaceDetails?.s_zip || data.s_zip || null,
      s_lat: selectedPlaceDetails?.s_lat || lat || null,
      s_lon: selectedPlaceDetails?.s_lon || lng || null,
      timezone: selectedPlaceDetails?.timezone || timezone || '',
    };

    const newPlaceDetail2 = {
      s_address1: noPhysicalAddress ? '' : data.s_address1 || addressInputValue,
      s_city: data.s_city,
      s_state: data.s_state,
      s_zip: data.s_zip,
      postal_code: data.s_zip,
      s_lat: lat,
      s_lon: lng,
      timezone,
    };

    const companyDetails = {
      user_name: data.fullName,
      email: data.email,

      country: userCountry === 'au' ? 'AU' : userCountry === 'ca' ? 'CA' : 'US',
      ...(billingType !== 0 ? { plan_id: selectedPlan } : {}),
      business_name: inputValue,
      physical_address: noPhysicalAddress ? false : true,
      etemplate_id: templateId,
      ...(manualAddressFields ||
      useSameEnterpriseDetails ||
      !selectedPlaceDetails ||
      noPhysicalAddress // Added this condition
        ? { ...newPlaceDetail2 }
        : { ...newPlaceDetail }),
    };

    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data.status === 200) {
          toast.success(data.message);
          handleClose();
        }
      },
    };

    createCompany(companyDetails, options);
  };

  const handleToggleCity = (e: any) => {
    setNoPhysicalAddress(!noPhysicalAddress);

    // Clear ALL address-related fields including city, state, and zip
    setValue('s_address1', '');
    setValue('s_city', '');
    setValue('s_state', '');
    setValue('s_zip', '');

    // Clear city selection states (important!)
    setSelectedCity(null);
    setInputValueCity('');
    setCityOption([]);

    setAddressOption(null);
    setSelectedAddress(null);
    setAddressInputValue('');
  };

  // Render helper for city selection

  const renderCitySelection = () => (
    <>
      <Autocomplete
        freeSolo
        fullWidth
        size="small"
        inputValue={inputValueCity}
        value={selectedCity}
        options={cityOption}
        getOptionLabel={(option: any) => option?.description || ''}
        filterOptions={(x) => x}
        onChange={(event, value: any) => {
          if (value?.place_id) {
            // Set the city value in the form
            setValue('s_city', value.description || '');
            setSelectedCity(value);
            fetchPlaceDetails(value?.place_id);
          } else if (typeof value === 'string') {
            // Handle freeSolo input
            setValue('s_city', value);
            setSelectedCity(null);
          } else {
            setValue('s_city', '');
            setSelectedCity(null);
          }
        }}
        onInputChange={(event, newInputValue) => {
          setInputValueCity(newInputValue);
          // Also update form value for freeSolo typing
          if (!selectedCity) {
            setValue('s_city', newInputValue);
          }
          fetchLocations(newInputValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="City"
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': { fontSize: '14px' },
              background: 'white',
            }}
          />
        )}
        PopperComponent={(props) => (
          <Popper {...props} sx={{ zIndex: 1300 }}>
            {props.children}
          </Popper>
        )}
        PaperComponent={(props) => (
          <Paper
            {...props}
            sx={{
              '& .MuiAutocomplete-noOptions': {
                fontSize: '14px',
                padding: '10px',
              },
              '& .MuiAutocomplete-option': { fontSize: '14px', padding: '5px' },
              borderRadius: 5,
              backgroundColor: '#f9f9f9',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            }}
          >
            {props.children}
          </Paper>
        )}
      />

      {errorsText?.city && (
        <span style={{ color: 'red', margin: '5px 10px', fontSize: '12px' }}>
          {errorsText.city}
        </span>
      )}

      {renderFormField('s_state', 'State')}
    </>
  );

  // Render helper for manual address fields
  const renderManualAddressFields = () => (
    <>
      {renderFormField('s_city', 'City', false, (field, e) => {
        field.onChange(e.target.value);
        if (useSameEnterpriseDetails) {
          const enterpriseSplitedEmail = authData?.user?.email.split('@');
          const sanitizedValue = e.target.value
            .replace(/\s+/g, '')
            .toLowerCase();
          if (enterpriseSplitedEmail) {
            setValue(
              'email',
              `${enterpriseSplitedEmail[0]}+${sanitizedValue}@${enterpriseSplitedEmail[1]}`,
            );
          }
          setInputValue(`${enterprise?.name} ${e.target.value}`);
        }
      })}
      {renderFormField('s_state', 'State')}
      {renderFormField('s_zip', 'ZIP Code', true)}
    </>
  );

  const renderFormField = (
    name: keyof FormData,
    label: string,
    isNumeric = false,
    customOnChange?: (field: any, e: any) => void,
  ) => (
    <FormControl sx={{ mt: '10px' }} fullWidth>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={label}
            size="small"
            variant="outlined"
            error={!!(errors as any)[name]}
            helperText={(errors as any)[name]?.message}
            fullWidth
            {...(isNumeric && {
              inputProps: { inputMode: 'numeric', pattern: '[0-9]*' },
              onChange: (e) => {
                const digitsOnly = e.target.value.replace(/\D/g, '');
                field.onChange(digitsOnly);
              },
            })}
            {...(customOnChange && {
              onChange: (e) => customOnChange(field, e),
            })}
            {...(!isNumeric &&
              !customOnChange &&
              name === 's_city' && {
                onChange: (e) => {
                  const value = e.target.value;
                  field.onChange(value);

                  // If s_city is cleared, also clear related fields and errors
                  if (!value || value.trim() === '') {
                    setValue('s_state', '');
                    setValue('s_zip', '');

                    // Clear form validation errors
                    clearErrors(['s_city', 's_state', 's_zip']);

                    // Clear custom error text
                    setErrorsText((prevState: any) => {
                      if (prevState) {
                        const updatedErrors = { ...prevState };
                        delete updatedErrors.city;
                        delete updatedErrors.s_state;
                        delete updatedErrors.zip;
                        return Object.keys(updatedErrors).length > 0
                          ? updatedErrors
                          : null;
                      }
                      return prevState;
                    });
                  }
                },
              })}
          />
        )}
      />

      {errorsText &&
        (errorsText as any)[
          name === 's_city' ? 'city' : name === 's_zip' ? 'zip' : name
        ] && (
          <span style={{ color: 'red', margin: '5px 10px', fontSize: '12px' }}>
            {
              (errorsText as any)[
                name === 's_city' ? 'city' : name === 's_zip' ? 'zip' : name
              ]
            }
          </span>
        )}
    </FormControl>
  );

  useEffect(() => {
    if (
      paymentInfo &&
      paymentInfo?.object &&
      paymentInfo?.object?.data?.length > 0 &&
      (authData?.user?.billing_cycle ?? 0 > 0)
    ) {
      setHasPaymentMethod(true);
    }
  }, [paymentInfo]);

  useEffect(() => {
    if (manualAddressFields || useSameEnterpriseDetails) {
      setNoPhysicalAddress(false);
    } else {
      setInputValue('');
    }
  }, [manualAddressFields, useSameEnterpriseDetails]);

  useEffect(() => {
    if (inputValue && !useSameEnterpriseDetails) {
      setResetInputValue(inputValue);
    }
  }, [inputValue]);

  return (
    <Dialog open onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          padding: 0,
        }}
      >
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <AddPlusIcon />
        <br />
        <Typography variant="h4">
          Add Company
          {templateName ? (
            <>
              for <strong>{templateName}</strong>
            </>
          ) : (
            ''
          )}
        </Typography>
        <FormControlLabel
          control={<Checkbox checked={useSameEnterpriseDetails} />}
          label="Copy Enterprise Admin Info"
          onChange={(e) => {
            setUseSameEnterpriseDetails(!useSameEnterpriseDetails);
            enterprise?.name && setInputValue(enterprise?.name);
            authData?.user?.name && setValue('fullName', authData?.user?.name);
          }}
        />
      </DialogTitle>
      <DialogContent>
        {errorText && (
          <Alert sx={{ margin: '10px 0' }} severity="error">
            {errorText}
          </Alert>
        )}
        <form
          onSubmit={handleSubmit(handleAdd)}
          style={{ marginBottom: '15px !important' }}
        >
          <EnterpriseDetailsSection
            useSameEnterpriseDetails={useSameEnterpriseDetails}
            control={control}
            errors={errors}
            inputValue={inputValue}
            setInputValue={setInputValue}
            valueOption={valueOption}
            setValueOption={setValueOption}
            fetchPlaceDetails={fetchPlaceDetails}
            fetchLocations={fetchLocations}
            setSelectedPlaceDetails={setSelectedPlaceDetails}
            errorsText={errorsText}
            options={options}
            handleToggleCity={handleToggleCity}
            noPhysicalAddress={noPhysicalAddress}
            selectedPlaceDetails={selectedPlaceDetails}
            resetInputValue={resetInputValue}
            setResetInputValue={setResetInputValue}
            manualAddressFields={manualAddressFields}
          />

          {selectedPlaceDetails && (
            <FormControl
              sx={{ marginBottom: '10px' }}
              size="small"
              variant="outlined"
              fullWidth
            >
              <TextField
                size="small"
                type="text"
                variant="outlined"
                fullWidth
                sx={{ backgroundColor: '#f1f1f1' }}
                value={`${selectedPlaceDetails.name}, ${selectedPlaceDetails.s_city}, ${selectedPlaceDetails.s_state} ${selectedPlaceDetails.s_zip}`}
                InputProps={{
                  readOnly: true,
                }}
              />
            </FormControl>
          )}
          <>
            {!noPhysicalAddress && (
              <FormControl sx={{ marginBottom: '10px' }} fullWidth>
                <Autocomplete
                  fullWidth
                  size="small"
                  sx={{ marginBottom: '10px' }}
                  inputValue={addressInputValue}
                  onChange={(event, value: any) => {
                    if (value) {
                      fetchPlaceDetails(value.place_id, 'fetchAddress');
                    } else {
                      setAddressOption(null);
                      setSelectedAddress(null);
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    setAddressInputValue(newInputValue ? newInputValue : '');
                    fetchLocations(newInputValue, 'fetchAddress');
                  }}
                  filterOptions={(x) => x}
                  freeSolo
                  value={
                    addressOption && Object.keys(addressOption).length > 0
                      ? addressOption
                      : addressInputValue
                        ? { description: addressInputValue }
                        : { description: '' }
                  }
                  //@ts-ignore
                  options={addressesOptions}
                  getOptionLabel={(option: any) => option.description}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Street Address"
                      variant="outlined"
                    />
                  )}
                />

                {errorsText && errorsText.address && (
                  <span
                    style={{
                      color: 'red',
                      margin: '-5px 10px 0 10px',
                      fontSize: '12px',
                    }}
                  >
                    {errorsText.address}
                  </span>
                )}
              </FormControl>
            )}

            {noPhysicalAddress === false &&
              selectedPlaceDetails &&
              Object.keys(selectedPlaceDetails)?.length > 0 &&
              selectedPlaceDetails?.name !== undefined &&
              manualAddressFields === false &&
              selectedPlaceDetails?.s_city === '' &&
              selectedPlaceDetails?.s_city !== undefined &&
              renderFormField('s_city', 'City', false)}

            {noPhysicalAddress === false &&
              selectedPlaceDetails &&
              Object.keys(selectedPlaceDetails)?.length > 0 &&
              selectedPlaceDetails?.name !== undefined &&
              manualAddressFields === false &&
              selectedPlaceDetails?.s_state === '' &&
              selectedPlaceDetails?.s_state !== undefined &&
              renderFormField('s_state', 'State')}

            {noPhysicalAddress === false &&
              selectedPlaceDetails &&
              Object.keys(selectedPlaceDetails)?.length > 0 &&
              selectedPlaceDetails?.name !== undefined &&
              manualAddressFields === false &&
              selectedPlaceDetails?.s_zip === '' &&
              selectedPlaceDetails?.s_zip !== undefined &&
              renderFormField('s_zip', 'ZIP Code', true)}

            {noPhysicalAddress
              ? renderCitySelection()
              : manualAddressFields
                ? renderManualAddressFields()
                : null}
          </>
          {billingType !== 0 && (
            <Box mt={2}>
              <Typography>Select Plan: </Typography>
              <RadioGroup value={selectedPlan} onChange={handlePlanChange} row>
                <FormControlLabel
                  value="4"
                  control={<Radio />}
                  label={PLANS.STARTER.NAME}
                />
                <FormControlLabel
                  value="1"
                  control={<Radio />}
                  label={PLANS.SMARTER.NAME}
                />
                <FormControlLabel
                  value="2"
                  control={<Radio />}
                  label={PLANS.PRO.NAME}
                />
              </RadioGroup>
            </Box>
          )}

          <LoadingButton
            loading={isCreatingCompany}
            fullWidth
            startIcon={<Add />}
            variant="contained"
            color="primary"
            type="submit"
            sx={{ marginTop: 3, marginBottom: 2 }}
          >
            Add Company
          </LoadingButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddCompanyModal;
